/**
 * Банер «На пристрої Europe/Warsaw, у профілі Europe/Kyiv. Оновити профіль?» (PRF-04): пояс
 * пристрою інший, ніж чинний пояс людини. Мовчазного перемикання немає: у відрядженні нагадування
 * «стрибали» б. Вибір: «Оновити профіль» (`PATCH /me`), «Не зараз» (до наступного сеансу) і «Більше
 * не питати для цього пристрою» (`prefs.tzHintOff`, лише цей пристрій).
 *
 * Пояси порівнюємо за зсувом від UTC зараз, а не за назвою IANA: `Europe/Kyiv` і `Europe/Zaporozhye`
 * чи два міста з однаковим часом не розбіжність, людині нема що вирішувати. Показуємо людську назву
 * місця (`Варшава`), а не сиру назву IANA.
 *
 * Показуємо після того, як відповіли `/me` і профіль компанії: до того чинний пояс — лише кеш, і
 * порівнювати з ним рано. Тільки для тих, хто ввійшов. Тут — лише виявлення; банер, дії й тексти —
 * у лінивому `RegionBannerBody.tsx` (#1244).
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, useSyncExternalStore } from 'react';
import { getFormatContext, subscribeFormatContext, zoneOffsetMinutes } from '../i18n/format.ts';
import { companyProfileQuery } from '../i18n/region.ts';
import { deviceZone } from '../i18n/zones.ts';
import { useFirstScreenDone } from '../perf/first-screen.ts';
import { saveMe } from '../platform/me-write.ts';
import { hasSessionHint } from '../platform/session.ts';
import { usePrefs } from '../prefs/prefs.ts';
import { meQuery } from './user.ts';

const now = () => Date.now();

type Body = typeof import('./RegionBannerBody.tsx')['RegionBannerBody'];

/**
 * Банер, його дії й тексти — лінивий `RegionBannerBody.tsx` (#1244): вантажимо, лише коли пояси
 * розходяться. Збій `import()` оболонку не ламає: банера немає, наступна розбіжність пробує знову.
 */
function useBody(needed: boolean): Body | null {
  const [body, setBody] = useState<Body | null>(null);
  useEffect(() => {
    if (!needed || body) return;
    let live = true;
    import('./RegionBannerBody.tsx').then(
      (m) => live && setBody(() => m.RegionBannerBody),
      () => undefined,
    );
    return () => {
      live = false;
    };
  }, [needed, body]);
  return body;
}

export function RegionBanner() {
  const queryClient = useQueryClient();
  const device = deviceZone();
  const signedIn = hasSessionHint();
  // `/me` і профіль компанії — після межі першого екрана, як у оболонці (i18n/region.ts).
  const firstScreen = useFirstScreenDone();
  const enabled = signedIn && firstScreen;
  const me = useQuery({ ...meQuery(), enabled });
  const company = useQuery({ ...companyProfileQuery(), enabled });
  const prefs = usePrefs();
  const ctx = useSyncExternalStore(subscribeFormatContext, getFormatContext, getFormatContext);
  const [dismissed, setDismissed] = useState(false);

  const profile = ctx.timeZone;
  // Чинний пояс остаточний, коли відповіли `/me` і (якщо власного поясу немає) профіль компанії.
  const known = me.data !== undefined && (me.data.timeZone !== null || !company.isPending);
  const differs =
    device !== null &&
    device !== profile &&
    zoneOffsetMinutes(device, now()) !== zoneOffsetMinutes(profile, now());
  const wanted = signedIn && known && differs && !prefs.tzHintOff && !dismissed;
  const Body = useBody(wanted);
  const mine = me.data;
  if (!wanted || !Body || !mine || !device) return null;
  return (
    <Body
      save={() => saveMe(queryClient, mine, { timeZone: device })}
      device={device}
      profile={profile}
      onDismiss={() => setDismissed(true)}
    />
  );
}
